// Passkey enrollment + login ceremonies.
//
// Wraps @simplewebauthn/browser; talks to the Worker's /auth/* endpoints.
// On success, the Worker sets the session cookie (SameSite=None) and the
// SPA persists the recipient's email in localStorage so subsequent visits
// can short-circuit straight to the login prompt.

import {
  startAuthentication,
  startRegistration,
} from "@simplewebauthn/browser";

import { postJson } from "./api";

const EMAIL_LS_KEY = "es_portal_email";

export function rememberEmail(email: string): void {
  localStorage.setItem(EMAIL_LS_KEY, email);
}

export function recallEmail(): string | null {
  return localStorage.getItem(EMAIL_LS_KEY);
}

export function forgetEmail(): void {
  localStorage.removeItem(EMAIL_LS_KEY);
}

// The Worker returns the WebAuthn options directly; @simplewebauthn/browser
// v11+ wraps them in { optionsJSON } before passing to the ceremony helpers.
// #94: enrollment + login are email-only (a passkey is an identity, not a
// per-project credential); the response carries only the email.
interface EnrollOptionsResponse {
  options: unknown;
  email: string;
}

export async function enrollWithTicket(ticketId: string): Promise<{
  email: string;
}> {
  const optsRes = await postJson<EnrollOptionsResponse>(
    "/auth/enroll/options",
    { ticket_id: ticketId },
  );
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const attestation = await startRegistration({ optionsJSON: optsRes.options as any });
  const completeRes = await postJson<{ email: string }>(
    "/auth/enroll/complete",
    { ticket_id: ticketId, response: attestation },
  );
  rememberEmail(completeRes.email);
  return completeRes;
}

interface LoginOptionsResponse {
  options: unknown;
}

export async function loginWithPasskey(email: string): Promise<{
  email: string;
}> {
  const optsRes = await postJson<LoginOptionsResponse>(
    "/auth/login/options",
    { email },
  );
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const assertion = await startAuthentication({ optionsJSON: optsRes.options as any });
  const verifyRes = await postJson<{ email: string }>(
    "/auth/login/verify",
    { email, response: assertion },
  );
  rememberEmail(verifyRes.email);
  return verifyRes;
}

export async function logout(): Promise<void> {
  await postJson("/auth/logout", {}).catch(() => undefined);
  forgetEmail();
}
